<script>
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  PieSeries,
  AccumulationLegend,
  AccumulationDataLabel,
  AccumulationTooltip,
} from '@syncfusion/ej2-vue-charts';

import { resolutionReasonsMetricColors } from '../../../../../../../../../shared/helpers/CustomDashboardsHelper.js';

const smaller_1_Sizes = {
  containerWidth: '295px',
  containerHeight: '115px',
  containerPaddingTop: '40px',
  centerLabelTextSize: '9px',
  dataLabelTextSize: '8px',
  startAngle: '110',
};

const smaller_2_Sizes = {
  containerWidth: '350px',
  containerHeight: '140px',
  containerPaddingTop: '40px',
  centerLabelTextSize: '10px',
  dataLabelTextSize: '10px',
  startAngle: '150',
};

const mediumSizes = {
  containerWidth: '340px',
  containerHeight: '190px',
  containerPaddingTop: '0px',
  centerLabelTextSize: '11px',
  dataLabelTextSize: '11px',
  startAngle: '30',
};

const biggerSizes = {
  containerWidth: '550px',
  containerHeight: '250px',
  containerPaddingTop: '0px',
  centerLabelTextSize: '15px',
  dataLabelTextSize: '15px',
  startAngle: '45',
};

export default {
  components: {
    AccumulationChartComponent,
    AccumulationSeriesCollectionDirective,
    AccumulationSeriesDirective,
  },
  provide: {
    accumulationchart: [
      AccumulationLegend,
      PieSeries,
      AccumulationTooltip,
      AccumulationDataLabel,
    ],
  },
  props: {
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    currentWidth: {
      type: Number,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    multipleSubmetrics: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      theme: 'Material',
      chartArea: {
        border: {
          width: 0,
        },
      },
      radius: '70%',
      border: { width: 1 },
      legendSettings: {
        textWrap: 'Wrap',
        visible: true,
        position: 'Right',
        alignment: 'Center',
        toggleVisibility: false, // Impede que a legenda seja clicável
      },
      tooltip: {
        enable: true,
        duration: 200,
        // eslint-disable-next-line no-template-curly-in-string
        format: '${point.x} : <b>${point.y}</b>',
      },
    };
  },
  computed: {
    seriesData() {
      const typesArray = this.submetricsSelecteds;

      const objectiveAchieved = typesArray.includes('objective_achieved')
        ? this.countResolutions('objective_achieved')
        : 0;

      const objectiveNotAchieved = typesArray.includes('objective_not_achieved')
        ? this.countResolutions('objective_not_achieved')
        : 0;

      const doubt = typesArray.includes('doubt')
        ? this.countResolutions('doubt')
        : 0;

      const other = typesArray.includes('other')
        ? this.countResolutions('other')
        : 0;

      const submetrics = [
        {
          t: 'objective_achieved',
          Type: this.objectiveAchievedLabel(),
          Resolutions: objectiveAchieved,
          DataLabelMappingName: `${this.getPercentage(objectiveAchieved)}%`,
        },
        {
          t: 'objective_not_achieved',
          Type: this.objectiveNotAchievedLabel(),
          Resolutions: objectiveNotAchieved,
          DataLabelMappingName: `${this.getPercentage(objectiveNotAchieved)}%`,
        },
        {
          t: 'doubt',
          Type: this.doubtLabel(),
          Resolutions: doubt,
          DataLabelMappingName: `${this.getPercentage(doubt)}%`,
        },
        {
          t: 'other',
          Type: this.otherLabel(),
          Resolutions: other,
          DataLabelMappingName: `${this.getPercentage(other)}%`,
        },
      ];

      let submetricsData = [];
      submetrics.forEach(item => {
        if (typesArray.includes(item.t)) {
          const { t, ...rest } = item;
          submetricsData.push(rest);
        }
      });

      return submetricsData;
    },
    centerLabel() {
      const textWhenNotHovered = 'Total <br>' + this.totalResolutions;

      // Header -> '<b>${point.x}<b>' | Valor -> '<b>${point.y}<b>'
      // eslint-disable-next-line no-template-curly-in-string
      const textWhenHovered = '${point.x} <br> ${point.y}';

      return {
        text: textWhenNotHovered,
        hoverTextFormat: textWhenHovered,
        textStyle: {
          fontWeight: '600',
          size: this.responsiveSizes.centerLabelTextSize,
        },
      };
    },
    dataLabel() {
      return {
        visible: true,
        name: 'DataLabelMappingName',
        position: 'Outside',
        font: {
          fontWeight: '600',
          size: this.responsiveSizes.dataLabelTextSize,
        },
        connectorStyle: {
          length: '0.5px',
          type: 'Line', // Possível colocar 'Curve'
        },
      };
    },
    submetricsSelecteds() {
      return this.multipleSubmetrics.map(m => m.type);
    },
    totalResolutions() {
      const typesArray = this.submetricsSelecteds;
      let total = 0;
      typesArray.forEach(item => {
        total += this.countResolutions(item);
      });
      return total;
    },
    responsiveSizes() {
      if (this.sizeY < 2) {
        if (this.currentWidth < 350) return smaller_1_Sizes;
        return smaller_2_Sizes;
      }
      if (this.currentWidth < 500) return mediumSizes;
      return biggerSizes;
    },
    shouldReduceFontSize() {
      const smallScreen = this.windowWidth < 1400;
      const smallCell = this.sizeY === 1;
      return smallScreen && smallCell;
    },
    noDataFound() {
      return this.seriesData.every(seriesItem => seriesItem.Resolutions === 0);
    },
  },
  mounted() {
    window.addEventListener('resize', this.updateWindowDimensions);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  },
  methods: {
    pointRender(args) {
      const pointType = String(args.point.x);
      if (pointType === this.objectiveAchievedLabel()) {
        args.fill = resolutionReasonsMetricColors.objectiveAchieved;
      } else if (pointType === this.objectiveNotAchievedLabel()) {
        args.fill = resolutionReasonsMetricColors.objectiveNotAchieved;
      } else if (pointType === this.doubtLabel()) {
        args.fill = resolutionReasonsMetricColors.doubt;
      } else {
        args.fill = resolutionReasonsMetricColors.other;
      }
    },
    generateUniqId() {
      return parseInt(Date.now() * Math.random().toFixed(5), 10);
    },
    countResolutions(type) {
      if (!this.data.inboxes_summary) return 0;
      return this.data.inboxes_summary.reduce((acc, inbox) => {
        if (!inbox.resolutions_summary) return acc;
        const count = inbox.resolutions_summary
          .filter(resolution => resolution.resolution_type === type)
          .reduce((sum, resolution) => sum + resolution.count, 0);
        return acc + count;
      }, 0);
    },
    getPercentage(count) {
      const total = this.totalResolutions;
      return total ? Math.round((count / total) * 100) : 0;
    },
    objectiveAchievedLabel() {
      return this.$t(
        'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OBJECTIVE_ACHIEVED'
      );
    },
    objectiveNotAchievedLabel() {
      return this.$t(
        'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OBJECTIVE_NOT_ACHIEVED'
      );
    },
    doubtLabel() {
      return this.$t('CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.DOUBT');
    },
    otherLabel() {
      return this.$t('CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OTHER');
    },

    updateWindowDimensions() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<template>
  <div v-if="noDataFound" class="text-xs">
    {{ $t('CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.NO_RESOLUTION') }}
  </div>
  <div
    v-else
    class="control-section"
    :style="{ 'padding-top': responsiveSizes.containerPaddingTop }"
  >
    <AccumulationChartComponent
      :id="`chartcontainer-${generateUniqId()}`"
      class="block-display"
      :theme="theme"
      align="center"
      :chart-area="chartArea"
      :legend-settings="legendSettings"
      :center-label="centerLabel"
      :point-render="pointRender"
      :enable-border-on-mouse-move="false"
      :tooltip="tooltip"
      :width="responsiveSizes.containerWidth"
      :height="responsiveSizes.containerHeight"
    >
      <AccumulationSeriesCollectionDirective>
        <AccumulationSeriesDirective
          :data-source="seriesData"
          :border="border"
          :radius="radius"
          x-name="Type"
          y-name="Resolutions"
          :start-angle="responsiveSizes.startAngle"
          :data-label="dataLabel"
          inner-radius="70%"
          tooltip-mapping-name="text"
          legend-shape="Circle"
        />
      </AccumulationSeriesCollectionDirective>
    </AccumulationChartComponent>
  </div>
</template>

<style scoped>
.block-display {
  display: block;
}
.donut-container {
  @apply text-slate-700;
}
</style>
